import React, {
    createRef, FormEvent, ReactElement, useCallback, useEffect, useState,
} from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { RiCloseLine } from "react-icons/ri";
import useKeyboardShortcuts, { useKeyboardState } from "../../contexts/KeyboardShortcutContext";
import useRouting, { isCurrentRoute } from "../../contexts/Routing";
import {
    SendFromType, useCreateUploadTokenMutation, useStartChatMutation,
} from "../../__generated__/api";
import { UploadedFile } from "../agent/text-entry/text-entry";
import Attachment from "../attachment/Attachment";
import Editor from "../editor/Editor";
import ConversationSources from "../pickers/conversation-sources/ConversationSources";
import Select, { Option, SelectInstanceHandler } from "../select/Select";
import TimedToast from "../timed-toast/TimedToast";
import Styles from "./StartConversation.module.scss";

const CONVERSATION_STATE = "create-conversations";
export default function StartConversation(): ReactElement {
    const { Paths, historyPush, createHistoryPush } = useRouting();
    const isComposeRoute = isCurrentRoute(Paths.AGENT_COMPOSE);

    const [conversationSourceIds, setConversationSourceIds] = useState<string[]>();
    const [text, setText] = useState<string>();
    const [from, setFrom] = useState<SendFromType>(SendFromType.Me);
    const [sendChat] = useStartChatMutation();
    const [showCancelSend, setShowCancelSend] = useState<boolean>(false);
    const [lastPayload, setLastPayload] = useState<{to: string[], body: string, sendFrom: SendFromType}>();
    const [attachments, setAttachments] = useState<UploadedFile[]>([]);

    const selectRef = createRef<SelectInstanceHandler>();
    const keyboardState = useKeyboardState();

    useEffect(() => {
        selectRef.current?.focus();
        if (isComposeRoute) {
            keyboardState.pushContext(CONVERSATION_STATE);
        } else {
            keyboardState.popContext(CONVERSATION_STATE);
        }
    }, [isComposeRoute]);

    const [uploadAttachment] = useCreateUploadTokenMutation();

    const shouldShow = isComposeRoute && !showCancelSend;
    const shouldShowToast = showCancelSend;

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const files = await acceptedFiles.mapConcurrent(async (file: File): Promise<UploadedFile> => {
            const meta = await uploadAttachment({
                variables: {
                    attachment: {
                        filename: file.name,
                        size: file.size,
                        mimeType: file.type,
                    },
                },
            });

            if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
                return {
                    name: file.name,
                    mimeType: file.type,
                    error: meta.data?.createUploadToken?.error ?? "Error uploading file",
                };
            }
            const { url, token } = meta.data.createUploadToken;

            const response = await fetch(url, {
                method: "POST",
                body: file,
                credentials: "include",
            });

            if (response.status !== 201) {
                return {
                    name: file.name,
                    mimeType: file.type,
                    error: "Error uploading file. Server did not return 201",
                };
            }

            const json = await response.json() as {url: string};

            return {
                name: file.name,
                mimeType: file.type,
                url: json.url,
                token,
            };
        });

        setAttachments(current => (current ?? []).concat(files));

        // Do something with the files
    }, []);
    const {
        getRootProps, getInputProps, isDragActive,
    } = useDropzone({ onDrop, noClick: true });

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "c",
            context: "default",
            group: "Conversations",
            description: "Start a new conversation",
            handler: createHistoryPush(Paths.AGENT_COMPOSE),
        });
        shortcuts.on({
            shortcut: "esc",
            context: CONVERSATION_STATE,
            handler: createHistoryPush(Paths.AGENT),
        });
    });

    const sendConversation = (data: string | undefined, event?: FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!data) {
            return;
        }
        const element = event?.target as HTMLFormElement;
        if (element && !element.checkValidity()) {
            element.reportValidity();
            return;
        }
        setLastPayload({
            body: text as string,
            sendFrom: from as SendFromType,
            to: conversationSourceIds as string[],
        });
        setShowCancelSend(true);
        setConversationSourceIds([]);
    };
    const cancelSend = () => {
        if (!lastPayload) {
            return;
        }
        setShowCancelSend(() => false);
        const { body, sendFrom, to } = lastPayload;
        setText(body);
        setConversationSourceIds(to);
        setFrom(sendFrom);
    };

    const sendPayload = () => {
        if (!lastPayload) {
            return;
        }
        const { body, sendFrom, to } = lastPayload;
        setLastPayload(undefined);
        setShowCancelSend(() => false);
        setAttachments([]);
        const attachmentTokens = attachments.toIdList("token").filterNull();
        (async () => {
            await sendChat({
                variables: {
                    message: {
                        body,
                        sendFrom,
                        to,
                        attachmentTokens,
                    },
                },
            });
        })();

        historyPush(Paths.AGENT);
    };

    return (
        <>
            {shouldShowToast && (
                <TimedToast title="Sending message..." seconds={3} onClose={sendPayload} canCancel onCancel={cancelSend}>
                    <div>
                        Sending message...
                    </div>
                </TimedToast>
            )}

            <Modal
                show={shouldShow}
                size="lg"
                onHide={createHistoryPush(Paths.AGENT)}
                fullscreen="lg-down"
                dialogClassName={`${Styles.container} card bg-white shadow border-0`}
                contentClassName="border-0 h-100"
                enforceFocus={false}
            >

                <form onSubmit={event => sendConversation(text, event)} className="h-100 w-100 m-0 p-0 d-flex flex-column">

                    <Modal.Body className="h-100 w-100 m-0 p-0 d-flex flex-column">
                        <div className="card-header bg-gradient text-white d-flex justify-content-between align-items-center">
                            <div>
                                Compose
                            </div>
                            <button type="button" className="btn btn-none m-0 p-1 px-2" onClick={createHistoryPush(Paths.AGENT)}>
                                <RiCloseLine className="text-light" />
                            </button>
                        </div>
                        <div className="flex-grow-1 p-3 d-flex flex-column" {...getRootProps()}>

                            {isDragActive && (
                                <div
                                    className="fs-4 text-light d-flex justify-content-center align-items-center flex-grow-1"
                                >
                                    Drag and Drop files here
                                </div>
                            )}

                            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                            <input {...getInputProps()} />
                            <div className={isDragActive ? "d-none" : "d-flex flex-column h-100"}>

                                <Form.Text>
                                    Who should this message be sent to?
                                </Form.Text>
                                <ConversationSources
                                    ref={selectRef}
                                    placeholder="To"
                                    onSelected={rows => setConversationSourceIds(rows)}
                                    isMulti
                                    minRequired={1}
                                    value={conversationSourceIds}
                                />

                                <Form.Text className="mt-3">
                                    Who should the message be sent from?
                                </Form.Text>

                                <Select
                                    minRequired={1}
                                    placeholder="From"
                                    onChange={(value: Option<unknown>) => setFrom(value.value as SendFromType)}
                                    value={{
                                        label: from === SendFromType.Me ? "Me" : "Agent",
                                        value: from,
                                    }}
                                    options={[
                                        {
                                            label: "Me",
                                            value: SendFromType.Me,
                                        },
                                        {
                                            label: "Agent",
                                            value: SendFromType.Agent,
                                        },
                                    ]}
                                />

                                <Form.Text className="mt-4">
                                    What is the message
                                </Form.Text>
                                <Editor
                                    onCommandEnterPressed={data => sendConversation(data)}
                                    className={`${Styles["text-container"]} p-0 pt-2 pb-2 px-2 mt-1 m-0 h-100 flex-grow-1`}
                                    onChange={setText}
                                    mentionSourceId={conversationSourceIds?.firstElement}
                                    text={lastPayload ? lastPayload.body : undefined}
                                />
                                {attachments?.isNotEmpty && (
                                    <div className="d-flex align-items-stretch flex-wrap">
                                        {attachments.map(attach => (
                                            <Attachment
                                                size="sm"
                                                className="mt-1 me-2"
                                                url={attach.url as string}
                                                mimeType={attach.mimeType}
                                                title={attach.name}
                                                onClose={() => setAttachments(attachments.filter(row => row.url !== attach.url))}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>

                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            className="modal-btn"
                            onClick={createHistoryPush(Paths.AGENT)}
                        >
                            Close
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="model-btn"
                            data-test="delete-btn"
                        >
                            Send
                        </Button>
                    </Modal.Footer>
                </form>

            </Modal>

        </>
    );
}
